import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ShieldCheck, ArrowRight, Award, Lock, Users } from "lucide-react";
import { SERVICES } from "../data/services";

const EXPLORE = [
  ["About Us", "/about"],
  ["Our Values", "/our-values"],
  ["Our Homes / Vacancies", "/vacancies"],
  ["For Participants", "/for-participants"],
  ["For Families", "/for-families"],
  ["For Coordinators", "/for-support-coordinators"],
  ["Make a Referral", "/referrals"],
  ["Easy Read", "/easy-read"],
  ["Resources", "/resources"],
  ["FAQ", "/faq"],
  ["Contact Us", "/contact"],
];

const POLICIES = [
  ["Code of Conduct", "/policies/code-of-conduct"],
  ["Complaints & Feedback", "/policies/complaints-feedback"],
  ["Decision Making", "/policies/decision-making"],
  ["Privacy & Dignity", "/policies/privacy-dignity"],
  ["Zero Tolerance", "/policies/zero-tolerance"],
  ["Privacy Policy", "/privacy"],
  ["Terms", "/terms"],
];

const TRUST = [
  { Icon: ShieldCheck, label: "NDIS Registered" },
  { Icon: Award, label: "Practice Standards" },
  { Icon: Users, label: "Worker Screened" },
  { Icon: Lock, label: "Privacy Compliant" },
];

export default function Footer() {
  return (
    <footer className="relative mt-20 overflow-hidden text-white" data-testid="site-footer">
      {/* Cinematic dark gradient backdrop — restrained */}
      <div className="absolute inset-0 bg-[#0F0625]" aria-hidden />
      <div className="absolute inset-0 bg-gradient-to-b from-[#1B0B3D] via-[#0F0625] to-[#0A0420]" aria-hidden />
      <div className="alpha-mesh alpha-mesh-dark" aria-hidden style={{ opacity: 0.5 }} />
      <div className="alpha-grain-dark" aria-hidden />
      <div className="absolute -left-32 top-10 w-[480px] h-[480px] rounded-full pointer-events-none alpha-glow-a"
        style={{ background: "radial-gradient(closest-side, rgba(134,89,199,0.4), transparent 70%)", filter: "blur(70px)" }} aria-hidden />
      <div className="absolute -right-24 bottom-0 w-[480px] h-[480px] rounded-full pointer-events-none alpha-glow-b"
        style={{ background: "radial-gradient(closest-side, rgba(79,165,110,0.32), transparent 70%)", filter: "blur(70px)" }} aria-hidden />

      {/* Top brand strip */}
      <div className="relative">
        <div className="alpha-container py-10 md:py-14 grid gap-8 md:grid-cols-12 items-center">
          <div className="md:col-span-5 flex items-center gap-4">
            <img
              src="/brand/logo-purple.jpg"
              alt="Alpha Community Care"
              className="h-24 md:h-28 w-auto rounded-2xl ring-1 ring-white/20 shadow-2xl"
              loading="lazy"
            />
            <div>
              <div className="font-display font-semibold text-xl leading-tight !text-white">Alpha Community Care</div>
              <div className="text-[11px] tracking-[0.22em] uppercase text-white/70 mt-1">Registered NDIS Provider</div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {TRUST.map((t) => (
                  <span key={t.label} className="alpha-glass-dark border border-white/15 text-white text-[10px] uppercase tracking-[0.18em] font-semibold rounded-full px-2.5 py-1 inline-flex items-center gap-1.5">
                    <t.Icon className="w-3 h-3 text-[color:var(--alpha-green-500)]" /> {t.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="md:col-span-4 text-white/85 text-sm leading-relaxed">
            Person-centred NDIS support across Australia — nationwide, with active hubs in Melbourne &amp; Perth. Built on independence, dignity, choice and collaboration.
          </div>
          <div className="md:col-span-3 flex md:justify-end flex-wrap gap-2">
            <Link to="/referrals" className="inline-flex items-center gap-2 bg-white text-[color:var(--alpha-purple-700)] rounded-full px-5 py-3 text-sm font-semibold shadow-2xl hover:scale-[1.02] transition-transform" data-testid="footer-refer">
              Make a Referral <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="tel:1300380692" className="alpha-glass-dark border border-white/30 text-white rounded-full px-5 py-3 text-sm font-semibold inline-flex items-center gap-2 hover:bg-white/15 transition-colors">
              <Phone className="w-4 h-4" /> 1300 380 692
            </a>
          </div>
        </div>
      </div>

      {/* Subtle divider */}
      <div className="relative h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />

      {/* Main link grid */}
      <div className="relative">
        <div className="alpha-container py-12 md:py-16 grid gap-10 md:gap-12 md:grid-cols-2 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <div className="alpha-overline !text-white/60 mb-4">Reach intake</div>
            <div className="space-y-3 text-sm text-white/85">
              <a href="tel:1300380692" className="flex items-center gap-2.5 hover:text-white" data-testid="footer-phone">
                <span className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center"><Phone className="w-4 h-4 text-[color:var(--alpha-green-500)]" /></span>
                <span><span className="font-display font-semibold !text-white text-base block leading-tight">1300 380 692</span><span className="text-xs text-white/60">Replies in 1 business day</span></span>
              </a>
              <a href="mailto:hello@alphacommunitycare.com.au" className="flex items-center gap-2.5 hover:text-white break-all" data-testid="footer-email">
                <span className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center"><Mail className="w-4 h-4 text-[color:var(--alpha-green-500)]" /></span>
                <span className="text-white/90">hello@alphacommunitycare.com.au</span>
              </a>
              <div className="flex items-start gap-2.5">
                <span className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center shrink-0"><MapPin className="w-4 h-4 text-[color:var(--alpha-green-500)]" /></span>
                <span className="text-white/85 mt-1.5">Docklands, Melbourne VIC<br />West Perth, WA</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="alpha-overline !text-white/60 mb-4">Services</div>
            <ul className="space-y-2.5">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="alpha-link-u text-sm text-white/80 hover:text-white" data-testid={`footer-svc-${s.slug}`}>{s.short}</Link>
                </li>
              ))}
            </ul>
            <div className="alpha-overline !text-white/60 mt-6 mb-3">Accommodation</div>
            <ul className="space-y-2.5">
              {SERVICES.slice(6).map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="alpha-link-u text-sm text-white/80 hover:text-white">{s.short}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="alpha-overline !text-white/60 mb-4">Explore</div>
            <ul className="space-y-2.5">
              {EXPLORE.map(([label, to]) => (
                <li key={to}>
                  <Link to={to} className="alpha-link-u text-sm text-white/80 hover:text-white" data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`}>{label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="alpha-overline !text-white/60 mb-4">Policies</div>
            <ul className="space-y-2.5">
              {POLICIES.map(([label, to]) => (
                <li key={to}>
                  <Link to={to} className="alpha-link-u text-sm text-white/80 hover:text-white">{label}</Link>
                </li>
              ))}
            </ul>
            <div className="mt-6">
              <Link to="/admin" className="text-xs text-white/55 hover:text-white">Admin</Link>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="relative border-t border-white/10">
        <div className="alpha-container py-5 flex flex-wrap items-center justify-between gap-3 text-xs text-white/60">
          <span>&copy; {new Date().getFullYear()} Alpha Community Care. All rights reserved.</span>
          <span className="max-w-2xl text-right md:text-left">We acknowledge the Traditional Custodians of the lands on which we work, and pay our respects to Elders past and present.</span>
        </div>
      </div>
    </footer>
  );
}
